import { envValue, type EnvRecord } from "./envPreflight";
import { parseMoneyCents, parseSoleMoneyCents, statedAmounts } from "./figures";
import { foldApostrophes } from "./scenario";
import type { EvidenceKind } from "./types";

/**
 * TA-1336 COMPETITOR MENTIONS (Kabir 2026-10-14, typed: "competitive analysis pricing algo, we need to do this
 * through messaging, and dms, but more descretely", recommendations approved "go ahead build").
 *
 * The pure half. A customer who says where they saw a piece, or what another shop asked, has handed the owner a
 * data point about the market. This file decides, in code, whether a message is worth asking the model about at
 * all (`competitorMentionPrefilter`), and then checks what the model said against the message itself
 * (`verifyCompetitorExtraction`). The model never does arithmetic and is never believed on its own word:
 *  - a shop or site name is kept only when it appears verbatim (case-insensitive) in the message;
 *  - a price is kept only when the shared money reader (F-16, figures.ts) reads it whole AND the message states
 *    exactly that amount, and only when the message states one amount, so the shop's own price quoted back is
 *    never mistaken for another shop's.
 * Precision beats recall (CLAUDE.md section 0): any doubt records nothing.
 *
 * WHAT A MENTION IS NOT. It is a customer claim (`dm.customer-claim`, weight 0.1), stored per shop. It is never a
 * KB fact, never quoted back to a customer, never read by a reply, and it never moves a price (T3, section 3).
 * The owner reads it in aggregate on the Results page and in the weekly digest, as advice only.
 */

export const ENABLE_COMPETITOR_INTEL_ENV = "ACKRA_ENABLE_COMPETITOR_INTEL";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateEnableCompetitorIntel(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (competitor mentions on) or "0" (off). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function competitorIntelRequested(env: EnvRecord): boolean {
  return envValue(env, ENABLE_COMPETITOR_INTEL_ENV) !== undefined && env[ENABLE_COMPETITOR_INTEL_ENV] === "1";
}

/** Exactly "1" is on; unset, empty or "0" is off; anything else throws (ACKRA_ENABLE_ANALYST's rule). Default OFF. */
export function planCompetitorIntel(env: EnvRecord): boolean {
  if (envValue(env, ENABLE_COMPETITOR_INTEL_ENV) === undefined) return false;
  const complaint = validateEnableCompetitorIntel(env[ENABLE_COMPETITOR_INTEL_ENV] ?? "");
  if (complaint !== null) throw new Error(`${ENABLE_COMPETITOR_INTEL_ENV} ${complaint}.`);
  return env[ENABLE_COMPETITOR_INTEL_ENV] === "1";
}

/** Where the customer saw or got the piece. Mirrored by the CHECK in migration 0065. */
export const COMPETITOR_SOURCE_KINDS = ["store", "website", "marketplace", "social", "other"] as const;
export type CompetitorSourceKind = (typeof COMPETITOR_SOURCE_KINDS)[number];

/**
 * The kind of piece. The catalog (0052) has no category column and no category vocabulary, so this is the list
 * TA-1336 ruled. Mirrored by the CHECK in migration 0065.
 */
export const COMPETITOR_PIECE_TYPES = [
  "engagement_ring",
  "wedding_band",
  "ring",
  "necklace",
  "pendant",
  "bracelet",
  "earrings",
  "watch",
  "other",
] as const;
export type CompetitorPieceType = (typeof COMPETITOR_PIECE_TYPES)[number];

/** The longest shop or site name kept, in characters. Mirrored by the CHECK in migration 0065. */
export const COMPETITOR_SOURCE_NAME_MAX = 80;

/** The longest message the extractor sends to the model; a longer one is skipped, not cut. AWAITING RATIFICATION. */
export const COMPETITOR_MESSAGE_MAX = 1000;

/** The one evidence kind a mention carries. Fixed by CHECK in the table. */
export const COMPETITOR_EVIDENCE_KIND: EvidenceKind = "dm.customer-claim";

export function isCompetitorSourceKind(v: unknown): v is CompetitorSourceKind {
  return typeof v === "string" && (COMPETITOR_SOURCE_KINDS as readonly string[]).includes(v);
}

export function isCompetitorPieceType(v: unknown): v is CompetitorPieceType {
  return typeof v === "string" && (COMPETITOR_PIECE_TYPES as readonly string[]).includes(v);
}

// ------------------------------------------------------------------ prefilter

/** "saw it at", "found it on", "got hers from": a verb of finding, then a place word within a short span. */
const FOUND_AT = /\b(?:saw|seen|see|seeing|found|find|spotted|got|get|bought|buy|ordered|looked|looking)\b[^.?!\n]{0,40}?\b(?:at|on|from)\b/i;
/** "from Marlowe": "from" then a capitalised word, read on the original casing. */
const FROM_NAME = /\bfrom\s+(?:the\s+)?[A-Z][\w&'.-]+/;
const URL_LIKE = /\bhttps?:\/\/|\bwww\.|\b[a-z0-9-]+\.(?:com|co|net|org|shop|store|jewelry|us|uk)\b/i;
const ANOTHER_SHOP = /\b(?:another|other|different)\s+(?:store|shop|jeweler|jeweller|jewelry store|jewellery store|place|site|website|brand|seller)s?\b/i;
const ONLINE = /\bonline\b/i;
const ELSEWHERE_WORD = /\b(?:there|they|their|elsewhere)\b/i;

/**
 * Whether a customer message plausibly names where they saw or got a piece, or another shop's price. Only a
 * message that passes is ever sent to the model, so most messages cost nothing. Deterministic; no money pattern
 * of its own (F-16): a price is found through figures.ts.
 */
export function competitorMentionPrefilter(text: string): boolean {
  const t = foldApostrophes(text);
  if (t.trim() === "") return false;
  if (FOUND_AT.test(t) || FROM_NAME.test(t) || URL_LIKE.test(t) || ANOTHER_SHOP.test(t) || ONLINE.test(t)) return true;
  const money = statedAmounts(t);
  return money.amounts.length + money.unreadable.length > 0 && ELSEWHERE_WORD.test(t);
}

/**
 * The shop's OWN channel, said in the second person or as the shop's: "your instagram", "ur site", "the shop's
 * page". The source question (`sourceQuestion.ts`, SAID_WHERE) reads it as "the customer already said where",
 * and the verifier below reads it as "this is not another shop" (TA-1336 review). One pattern, two readers.
 * Read on `foldApostrophes` text.
 */
export const OWN_CHANNEL =
  /\b(?:your|ur|the shop'?s|y'?all'?s)\s+(?:instagram|insta|ig|tiktok|facebook|fb|pinterest|website|site|story|stories|post|posts|reel|reels|ad|ads|page|feed|profile)\b/i;

// ------------------------------------------------------------------ verifier

/** What a mention records, after code has checked it. No message text. */
export interface VerifiedCompetitorMention {
  readonly sourceKind: CompetitorSourceKind;
  /** Verbatim from the message (the customer's own casing), or null. */
  readonly sourceName: string | null;
  readonly pieceType: CompetitorPieceType;
  /** Integer cents, > 0, read by the shared money reader, or null. */
  readonly claimedPriceCents: number | null;
  /** "USD" exactly when a price is present. */
  readonly currency: "USD" | null;
}

/** The exact keys the model must return, no more, no fewer. */
export const COMPETITOR_EXTRACT_KEYS = ["mentioned", "source_kind", "source_name", "piece_type", "price_text"] as const;

const EMAIL_LIKE = /@/;
const PHONE_LIKE = /\d[\d\s().-]{5,}\d/;

/** The message's own spelling of `name`, found case-insensitively, or null when it does not carry it. */
function verbatimIn(message: string, name: string): string | null {
  const at = message.toLowerCase().indexOf(name.toLowerCase());
  return at < 0 ? null : message.slice(at, at + name.length);
}

function keepName(message: string, value: string | null): string | null {
  if (value === null) return null;
  const name = value.trim();
  if (name === "" || name.length > COMPETITOR_SOURCE_NAME_MAX) return null;
  // A name is a shop or a site. An email or a phone-shaped string is a person's detail, never kept (section 10).
  if (EMAIL_LIKE.test(name) || PHONE_LIKE.test(name)) return null;
  return verbatimIn(message, name);
}

/** "your", "ur", "yours", "y'all's", "the shop's": the customer is talking about this shop. Folded text. */
const OWN_POSSESSIVE = /\b(?:your|ur|yours|y'?all'?s|the shop'?s)\b/i;
/** How many words before a kept name or price are read for an own possessive ("on ur new shop page, Bellmont"). */
const OWN_POSSESSIVE_WORDS = 4;

/** Apostrophes folded, accents folded ("Café" as "cafe", "Hélène" as "helene"), lower case. TA-1375. */
function fold(text: string): string {
  return foldApostrophes(text).normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase();
}

/**
 * Folded, a possessive "'s" dropped from each word, "&" set apart as a word of its own ("J&R" as "j & r",
 * TA-1375), other punctuation to spaces, padded.
 */
function wordsOf(text: string): string {
  const w = fold(text).replace(/'s\b/g, "").replace(/&/g, " & ").replace(/[^a-z0-9&]+/g, " ").trim();
  return ` ${w} `;
}

/**
 * TA-1375 round 2. 5f4596d8's `wordsOf`, unchanged: lower case, apostrophes folded, a possessive "'s" dropped,
 * anything outside a-z, 0-9 and "&" (an accented letter among them) to a space, padded. `isOwnShop` keeps it as
 * an extra hold so its own-shop reading is never narrower than base's. Never used to keep anything.
 */
function legacyWordsOf(text: string): string {
  return ` ${foldApostrophes(text).toLowerCase().replace(/'s\b/g, "").replace(/[^a-z0-9&]+/g, " ").trim()} `;
}

/**
 * TA-1336 review. Whether the mention is of the shop's OWN channel or the shop itself, so it is no data point
 * about the market at all: "saw it on your instagram for $4,500" is the shop's own page and its own price, and
 * recording it would tell the owner his range sits within itself. True when the message names an own channel
 * (`OWN_CHANNEL`), when an own possessive sits within a few words before the kept name or the kept price, or
 * when the message carries the deployment's shop name as whole words ("Nekta's", "NEKTA'S", "Nekta Jewelers"),
 * case-insensitive. TA-1375 widens the last test to the shorter forms a customer types: the name less its
 * generic ends ("J & R" for "J & R Jewelers"), any one distinctive word of it ("at Bellmont" against "Bellmont
 * Jewelers"), the name closed up or as the shop's own site ("bellmontjewelers.com"), and a one-letter
 * misspelling of a long distinctive word ("Belmont"). TA-1376 adds the shop's own site, handle or email
 * (`siteWordPieces`). A shared generic word such as "Jewelers" alone is not the shop. Wide on purpose: a false
 * hit only loses one data point (section 0).
 */
function isOwnShop(message: string, anchors: readonly (string | null)[], shopName: string): boolean {
  const folded = foldApostrophes(message);
  if (OWN_CHANNEL.test(folded)) return true;
  const lower = folded.toLowerCase();
  for (const anchor of anchors) {
    if (anchor === null) continue;
    const at = lower.indexOf(foldApostrophes(anchor).toLowerCase());
    if (at < 0) continue;
    const before = folded.slice(0, at).split(/\s+/).filter((w) => w !== "").slice(-OWN_POSSESSIVE_WORDS).join(" ");
    if (OWN_POSSESSIVE.test(before)) return true;
  }
  const shop = wordsOf(shopName);
  const said = wordsOf(message);
  if (shop.trim() !== "" && said.includes(shop)) return true;
  // TA-1375 round 2. 5f4596d8's reading, kept as a hold of its own so the accent folding above never loosens it:
  // shop "René" read as "ren" holds "Renée's" and "Ren", shop "Jos" holds "José".
  const legacyShop = legacyWordsOf(shopName);
  if (legacyShop.trim() !== "" && legacyWordsOf(message).includes(legacyShop)) return true;
  // TA-1375. The configured name is often longer than what the customer types ("Bellmont Jewelers" configured,
  // "saw it at Bellmont" typed). The name less its generic ends, or any one distinctive word of it, is the shop.
  if (corePhraseSaid(said, nameWords(shopName))) return true;
  const heard = messageTokens(message);
  const distinctive = distinctiveNameTokens(shopName);
  for (const w of distinctive) {
    for (const t of heard) if (sameNameWord(w, t)) return true;
  }
  // TA-1376. The shop's name anywhere inside a host or handle ("shopnekta.com", "@nektajewelry"); a path segment
  // or an email's own name only as the name plus generic or web words ("etsy.com/shop/NektaJewelry").
  for (const raw of foldApostrophes(message).split(/\s+/)) {
    const { host, named } = siteWordPieces(raw);
    if (host.some((piece) => distinctive.some((w) => piece.includes(w)))) return true;
    if (named.some((piece) => ownNameWithAffixes(piece, distinctive))) return true;
  }
  return false;
}

/** An optional scheme, a dotted host, an optional port, then any path. WIDE_DOTS: ideographic and fullwidth stops. */
const SITE_WORD = /^(?:https?:\/\/)?([a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,})(?::\d+)?([/?#].*)?$/;
const WIDE_DOTS = /[\u3002\uff0e\uff61]/g;

/** A message word's closed-up names: `host` is read by containment, `named` only by `ownNameWithAffixes`. */
type SitePieces = { readonly host: readonly string[]; readonly named: readonly string[] };
const closeUp = (t: string): string => t.replace(/[^a-z0-9]+/g, "");

/** A host's labels less a leading "www" and the last label, closed up ("shop.nekta-jewelry.co.uk" as "shopnektajewelryco"); the path's segments named. */
function hostPieces(host: string, path: string): SitePieces {
  const labels = host.replace(/^www\./, "").split(".").slice(0, -1);
  return { host: [closeUp(labels.join(""))], named: path.split(/[/?#&=]+/).map(closeUp) };
}

/**
 * TA-1376. A web address's host and path segments, a handle, or an email's own name and host, closed up; a typed
 * word gives nothing ("Bellmontage" is another shop). A host or handle is a name a shop owns and shows no word
 * breaks ("rosenektajewelry.com" may be "Rose Nekta Jewelry"): unsure, so held (B13). A path segment or an email's
 * own name sits under the host's owner ("bluenile.com/earrings", "nektalover@gmail.com"), so it is `named`.
 */
function siteWordPieces(raw: string): SitePieces {
  const trimEnd = (t: string): string => t.replace(/[^a-z0-9/]+$/, "");
  const w = trimEnd(trimEnd(fold(raw).replace(WIDE_DOTS, ".").replace(/^[^a-z0-9@]+/, "")).replace(/'s$/, ""));
  const at = w.lastIndexOf("@");
  const site = at === 0 ? null : SITE_WORD.exec(w.slice(at + 1));
  if (at < 0) return site === null ? { host: [], named: [] } : hostPieces(site[1] ?? "", site[2] ?? "");
  const own = site === null ? { host: [closeUp(w.slice(at + 1))], named: [] } : hostPieces(site[1] ?? "", site[2] ?? "");
  return at === 0 ? own : { host: own.host, named: [closeUp(w.slice(0, at)), ...own.named] };
}

/**
 * TA-1375. Words that name a kind of shop, not a shop: shared by many jewelers, so a competitor sharing only
 * these with the shop's name ("Marlowe Jewelers" against "Bellmont Jewelers", "Marlowe Studio" against
 * "Bellmont Studio") is still a competitor. Articles, joiners, company suffixes and the trade's own words.
 * AWAITING RATIFICATION (TA-1375 review): the list is the implementer's and the reviewer's reading.
 */
const GENERIC_NAME_WORDS: ReadonlySet<string> = new Set([
  "the", "a", "an", "and", "&", "of",
  "co", "company", "inc", "llc", "ltd",
  "jeweler", "jewelers", "jeweller", "jewellers", "jewelry", "jewellery", "jewel", "jewels",
  "diamond", "diamonds", "gem", "gems", "gold", "fine", "silver", "platinum",
  "store", "stores", "shop", "shops", "shoppe", "studio", "studios", "boutique", "gallery",
  "bridal", "design", "designs", "estate", "watch", "watches",
]);

/** TA-1376 review. Web words a shop's own path or email name may carry besides the generic words. Not ratified. */
const WEB_AFFIXES: readonly string[] = ["shop", "store", "official", "online", "the", "my"];
const AFFIX_WORDS: readonly string[] = [...new Set([...GENERIC_NAME_WORDS, ...WEB_AFFIXES])].filter((t) => /^[a-z0-9]+$/.test(t));

/**
 * Whether `piece` splits, end to end, into the shop's distinctive words and affix words, one distinctive word at
 * least ("shopnekta", "harlowandfinch", never "earrings" for "ring"). `reach[i]`: the first i letters split
 * with no name word (1), with one (2), or not at all (0).
 */
function ownNameWithAffixes(piece: string, distinctive: readonly string[]): boolean {
  const reach = [1, ...new Array<number>(piece.length).fill(0)];
  const words = [...AFFIX_WORDS, ...distinctive];
  for (let i = 1; i <= piece.length; i++) {
    for (const w of words) {
      const from = i >= w.length ? (reach[i - w.length] ?? 0) : 0;
      if (from > 0 && piece.startsWith(w, i - w.length)) reach[i] = Math.max(reach[i] ?? 0, distinctive.includes(w) ? 2 : from);
    }
  }
  return reach[piece.length] === 2;
}

/** The leading words dropped from a name made only of generic words ("The Gold Store" read as "gold store"). */
const ARTICLES: ReadonlySet<string> = new Set(["the", "a", "an"]);

/** The words that may stand just before an all-generic name in the message for it to read as that name. */
const LEAD_INS: ReadonlySet<string> = new Set(["at", "from", "on", "in", "by", "via", "to", "the", "a", "an", "called", "named"]);

/** The shop name's words as `wordsOf` reads them, "&" among them. */
function nameWords(name: string): readonly string[] {
  return wordsOf(name).trim().split(" ").filter((t) => t !== "");
}

/** Whether the name word at `i` is generic. A lone letter beside "&" is an initial ("A & B"), never the article. */
function isGenericAt(words: readonly string[], i: number): boolean {
  const t = words[i] ?? "";
  if (!GENERIC_NAME_WORDS.has(t)) return false;
  return !(t.length === 1 && t !== "&" && (words[i - 1] === "&" || words[i + 1] === "&"));
}

function alnumLength(words: readonly string[]): number {
  return words.join("").replace(/[^a-z0-9]/g, "").length;
}

/**
 * TA-1375 review. The name less its generic words at both ends ("J & R Jewelers" as "j & r"), as whole words
 * anywhere in the message. A name generic throughout ("The Gold Store") is read less its leading article
 * ("gold store"), and only where nothing but a lead-in word ("at", "the") stands before it, so "Marlowe Gold
 * Store" is still another shop. Either core needs two letters or digits.
 */
function corePhraseSaid(said: string, words: readonly string[]): boolean {
  let lo = 0;
  let hi = words.length;
  while (lo < hi && isGenericAt(words, lo)) lo++;
  while (hi > lo && isGenericAt(words, hi - 1)) hi--;
  if (lo < hi) {
    const core = words.slice(lo, hi);
    return alnumLength(core) >= 2 && said.includes(` ${core.join(" ")} `);
  }
  let a = 0;
  while (a < words.length && ARTICLES.has(words[a] ?? "")) a++;
  const core = words.slice(a);
  if (alnumLength(core) < 2) return false;
  const phrase = ` ${core.join(" ")} `;
  for (let at = said.indexOf(phrase); at >= 0; at = said.indexOf(phrase, at + 1)) {
    const before = said.slice(0, at).trim().split(" ").pop() ?? "";
    if (before === "" || LEAD_INS.has(before)) return true;
  }
  return false;
}

/** A site's suffix, dropped so "bellmontjewelers.com" reads as "bellmontjewelers". */
const WEB_SUFFIX = /\.(?:com|co|net|org|shop|store|jewelry|us|uk)(?:\.[a-z]{2})?$/;
/** A web address as one word: an optional scheme and "www.", a dotted host, then any path. */
const URL_HOST = /^(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,})(?:[/?#].*)?$/;

/**
 * Folded letters and digits only: apostrophes, hyphens and "&" closed up ("Bell-mont", "O'Brien", "J&R" read
 * whole), a possessive "'s" dropped, a leading "@" and a web address's scheme, "www.", path and suffix dropped.
 */
function compact(word: string): string {
  let w = fold(word).replace(/^[("'[]+/, "").replace(/[.,!?;:)"'\]]+$/, "").replace(/^@/, "");
  const url = URL_HOST.exec(w);
  if (url !== null) w = (url[1] ?? "").replace(WEB_SUFFIX, "");
  return w.replace(/'s$/, "").replace(/[^a-z0-9]+/g, "");
}

/**
 * The message's words, read two ways so a punctuation variant is not missed: split on any punctuation
 * (`wordsOf`, "Bellmont." and "Bellmont-Jewelers" as words) and split on spaces only with the punctuation
 * closed up (`compact`: "Bell-mont" as "bellmont", "J&R" as "jr", "BellmontJewelers.com" as "bellmontjewelers").
 */
function messageTokens(message: string): ReadonlySet<string> {
  const out = new Set<string>();
  for (const t of wordsOf(message).trim().split(" ")) if (t !== "&" && t !== "") out.add(t);
  for (const raw of foldApostrophes(message).split(/\s+/)) {
    const c = compact(raw);
    if (c !== "") out.add(c);
  }
  return out;
}

/**
 * The words of the shop's name that name THIS shop: every word not generic, read both ways as the message is,
 * longer than one letter (a lone initial is not a name); the non-generic words closed up into one ("J & R
 * Jewelers" as "jr"); and the whole name closed up when it has four letters or more ("bellmontjewelers").
 */
function distinctiveNameTokens(shopName: string): readonly string[] {
  const words = nameWords(shopName);
  const spaced = foldApostrophes(shopName).split(/\s+/).map(compact);
  const keep = (t: string): boolean => t.length > 1 && !GENERIC_NAME_WORDS.has(t);
  const out = new Set([...words.filter((_t, i) => !isGenericAt(words, i)), ...spaced].filter(keep));
  const joined = words.filter((t, i) => t !== "&" && !isGenericAt(words, i)).join("");
  if (keep(joined)) out.add(joined);
  const whole = words.filter((t) => t !== "&").join("");
  if (whole.length >= 4) out.add(whole);
  return [...out];
}

/** How long a name word must be before a one-letter misspelling of it ("Belmont") is read as the shop. */
const NEAR_NAME_MIN = 6;

/** True when `a` becomes `b` by one letter added, dropped or changed. */
function withinOneEdit(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1);
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/**
 * A name word and a message word are the same when equal, when one is the other plus a trailing "s"
 * ("Bellmonts"), or, both six letters or more, one letter apart ("Belmont" against "Bellmont": unsure, so
 * held, TA-1375 review).
 */
function sameNameWord(name: string, said: string): boolean {
  if (said === name || said === `${name}s` || name === `${said}s`) return true;
  return name.length >= NEAR_NAME_MIN && said.length >= NEAR_NAME_MIN && withinOneEdit(name, said);
}

/** The generic words that name a kind of place; "the" before one in the shop's own name reads as the shop. */
const PLACE_WORDS: ReadonlySet<string> = new Set([
  "store", "stores", "shop", "shops", "shoppe", "studio", "studios", "boutique", "gallery",
  "jeweler", "jewelers", "jeweller", "jewellers",
]);

/**
 * TA-1375 review. A kept name made only of generic words that are all in the shop's own name ("the studio"
 * against "Bellmont Studio", "Gold Store" against "The Gold Store"), or, whatever the model named, "the" and a
 * kind of place the shop's own name carries ("at the store" against "Bellmont Jewelry Store"), may be this
 * shop or any other: unsure, so nothing is recorded. "another store" and "Marlowe Studio" carry a word of their
 * own and are kept.
 */
function onlyShopTradeWords(message: string, sourceName: string | null, shopName: string): boolean {
  const shop = new Set(nameWords(shopName));
  const said = wordsOf(message);
  for (const place of PLACE_WORDS) if (shop.has(place) && said.includes(` the ${place} `)) return true;
  if (sourceName === null) return false;
  const named = nameWords(sourceName).filter((t) => t !== "&" && !ARTICLES.has(t));
  return named.length > 0 && named.every((t) => GENERIC_NAME_WORDS.has(t) && shop.has(t));
}

function keepPrice(message: string, priceText: string | null): number | null {
  if (priceText === null) return null;
  const cents = parseSoleMoneyCents(priceText);
  if (cents === null || cents <= 0) return null;
  if (verbatimIn(message, priceText.trim()) === null) return null;
  // The message must state that amount WHOLE (not "$4k", not one end of a range) and state no other amount:
  // with two amounts, which one is another shop's is a guess, and a guess records nothing.
  const stated = statedAmounts(message);
  if (stated.unreadable.length > 0) return null;
  const amounts = new Set(stated.amounts.map((a) => parseMoneyCents(a)));
  if (amounts.size !== 1 || !amounts.has(cents)) return null;
  return cents;
}

/**
 * The model's answer, checked against the message. Null records nothing: a malformed answer (not an object,
 * a missing or extra key, a value outside its closed set or of the wrong type), `mentioned: false`, an
 * `other` source with no name the message carries (too vague to be a data point), or a mention of the shop's
 * own channel or name (`isOwnShop`, TA-1336 review), or a kept name made only of the shop's own trade words
 * (`onlyShopTradeWords`, TA-1375 review). `shopName` is the deployment's display name, resolved by
 * the caller as the reply path does (`deploymentShopDisplayName`), so this stays pure.
 */
export function verifyCompetitorExtraction(message: string, value: unknown, shopName: string): VerifiedCompetitorMention | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length !== COMPETITOR_EXTRACT_KEYS.length || !COMPETITOR_EXTRACT_KEYS.every((k) => keys.includes(k))) return null;
  const mentioned = o["mentioned"];
  const kind = o["source_kind"];
  const piece = o["piece_type"];
  const name = o["source_name"];
  const price = o["price_text"];
  if (typeof mentioned !== "boolean") return null;
  if (!isCompetitorSourceKind(kind) || !isCompetitorPieceType(piece)) return null;
  if (name !== null && typeof name !== "string") return null;
  if (price !== null && typeof price !== "string") return null;
  if (!mentioned) return null;
  const sourceName = keepName(message, name);
  if (kind === "other" && sourceName === null) return null;
  const claimedPriceCents = keepPrice(message, price);
  if (isOwnShop(message, [sourceName, claimedPriceCents === null ? null : price], shopName)) return null;
  if (onlyShopTradeWords(message, sourceName, shopName)) return null;
  return { sourceKind: kind, sourceName, pieceType: piece, claimedPriceCents, currency: claimedPriceCents === null ? null : "USD" };
}

// ------------------------------------------------------------- the shop's side

/**
 * The piece type a catalog product's `specs.style` names, or null. The catalog has no category column, so this
 * maps only an exact name ("Engagement Ring", "engagement rings", "engagement_ring"); a style that only
 * contains one ("vintage engagement ring") is not mapped, because a guess at the shop's own range would be
 * advice built on a guess (section 0). TA-1336, AWAITING a catalog category (FINDINGS).
 */
export function pieceTypeOfStyle(style: string): CompetitorPieceType | null {
  const s = style.trim().toLowerCase().replace(/[\s_-]+/g, " ");
  if (s === "" || s === "other") return null;
  for (const p of COMPETITOR_PIECE_TYPES) {
    if (p === "other") continue;
    const name = p.replace(/_/g, " ");
    const singular = name.endsWith("s") ? name.slice(0, -1) : name;
    if (s === name || s === singular || s === `${singular}s`) return p;
  }
  return null;
}
